import React, { useCallback, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronDown, Mail } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../utils/cn';
import { NAV_ITEMS, COMPANY_NAME } from '../../constants';

// Import logo
import FullLogo from '../../assets/images/Full_logo.png';
// Fluid-sphere animation used as the header's Ask-AI CTA.
import fluidSphere from '../../assets/dynamic_fluid_sphere.gif';

const ASK_AI_SECTION_ID = 'ask-istatec-ai';

export const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const headerBg = 'bg-black';
  const textColor = 'text-white';

  /**
   * "Ask AI" CTA — scroll to the AskIstatecAI section on the home page.
   * If we're already on `/`, smooth-scroll in place. Otherwise navigate to
   * `/#ask-istatec-ai` and let the router (see ScrollToTop) hop to it.
   */
  const goToAskAI = useCallback(
    (extraOnClick?: () => void) => (e: React.MouseEvent) => {
      e.preventDefault();
      extraOnClick?.();
      if (location.pathname === '/') {
        const el = document.getElementById(ASK_AI_SECTION_ID);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          // Keep the hash in the URL for shareability without triggering a re-render.
          window.history.replaceState(null, '', `#${ASK_AI_SECTION_ID}`);
          return;
        }
      }
      navigate(`/#${ASK_AI_SECTION_ID}`);
    },
    [navigate, location.pathname],
  );

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        headerBg
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center group">
            <img
              src={FullLogo}
              alt={COMPANY_NAME}
              className="h-12 object-contain rounded-lg transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_ITEMS.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && setOpenDropdown(item.label)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={item.href}
                  className={cn(
                    'flex items-center gap-1 px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                    textColor,
                    'hover:bg-white/10'
                  )}
                >
                  {item.label}
                  {item.children && <ChevronDown className="w-4 h-4" />}
                </Link>

                {/* Dropdown Menu */}
                <AnimatePresence>
                  {item.children && openDropdown === item.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute top-full left-0 pt-2 w-64"
                    >
                      <div className="bg-white rounded-xl shadow-xl border border-navy-100 py-2 overflow-hidden">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className="block px-4 py-3 text-sm text-navy-700 hover:bg-navy-50 hover:text-navy-900 transition-colors"
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Contact mail icon + Ask-AI sphere CTA (desktop) */}
          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/contact"
              aria-label="Contact us"
              title="Contact us"
              className={cn(
                'group relative flex items-center justify-center w-10 h-10 rounded-full',
                'text-white/90 hover:text-white',
                'bg-white/5 hover:bg-white/15 border border-white/15 hover:border-white/30',
                'transition-all'
              )}
            >
              <Mail className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-accent-500 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>

            {/* Ask Istatec AI — fluid-sphere CTA */}
            <a
              href={`/#${ASK_AI_SECTION_ID}`}
              onClick={goToAskAI()}
              aria-label="Ask Istatec AI"
              title="Ask Istatec AI"
              className={cn(
                'group relative inline-flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full',
                'bg-white/5 hover:bg-white/15 border border-white/15 hover:border-white/30',
                'transition-all',
              )}
            >
              <span className="relative flex items-center justify-center w-9 h-9 rounded-full overflow-hidden bg-navy-900 ring-1 ring-white/20">
                <img
                  src={fluidSphere}
                  alt=""
                  aria-hidden="true"
                  className="w-9 h-9 object-cover"
                />
              </span>
              <span className="text-sm font-medium text-white/90 group-hover:text-white">
                Ask AI
              </span>
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full blur-md opacity-0 group-hover:opacity-40 bg-accent-500/40 transition-opacity"
              />
            </a>
          </div>

          {/* Mobile: mail icon + menu toggle */}
          <div className="flex items-center gap-2 lg:hidden">
            <Link
              to="/contact"
              aria-label="Contact us"
              title="Contact us"
              className={cn(
                'flex items-center justify-center w-9 h-9 rounded-full',
                'text-white/90 hover:text-white',
                'bg-white/5 hover:bg-white/15 border border-white/15',
                'transition-all'
              )}
            >
              <Mail className="w-4 h-4" />
            </Link>
            <button
              className={cn('p-2 rounded-lg', textColor)}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-black border-t border-white/10 overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-4 py-4">
              {NAV_ITEMS.map((item) => (
                <div key={item.label} className="border-b border-white/10 last:border-0">
                  <Link
                    to={item.href}
                    className="block py-3 text-white font-medium"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <div className="pl-4 pb-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.label}
                          to={child.href}
                          className="block py-2 text-sm text-navy-200 hover:text-white"
                          onClick={() => setIsMobileMenuOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <a
                href={`/#${ASK_AI_SECTION_ID}`}
                onClick={goToAskAI(() => setIsMobileMenuOpen(false))}
                aria-label="Ask Istatec AI"
                className="mt-4 flex items-center justify-center gap-3 w-full py-3 rounded-lg bg-white/5 border border-white/15 hover:bg-white/10 transition-colors"
              >
                <span className="relative flex items-center justify-center w-9 h-9 rounded-full overflow-hidden bg-navy-900 ring-1 ring-white/20">
                  <img
                    src={fluidSphere}
                    alt=""
                    aria-hidden="true"
                    className="w-9 h-9 object-cover"
                  />
                </span>
                <span className="text-white font-medium">Ask Istatec AI</span>
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

